import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { Ship, ManufacturerResponse } from '../../interfaces/types';
import { ShipList } from '../../components/ships/ShipList';
import './ShipsPage.css';
import '../../components/common/MenusLayout.css';
import { FaCaretDown, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { Navigation } from '../../components/common/Navigation';
import { SearchBar } from '../../components/ships/SearchBar';
import { Pagination } from '../../components/common/Pagination';
import { ShipComparison } from '../../components/ships/ShipComparison';
import { ShipCard } from '../../components/ships/ShipCard';
import { ShipCatalogView } from '../../components/ships/ShipCatalogView';
import { ProtectedRoute } from '../../components/auth/ProtectedRoute';
import { Header } from '../../components/common/Header';
import { useUser } from '../../hooks/useUser';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/common/ToastContainer';
import { TYPE_TRANSLATIONS, SIZE_TRANSLATIONS, PRODUCTION_STATUS_TRANSLATIONS } from '../../utils/translations';

const SORT_OPTIONS = {
  'name': 'Nombre',
  'size': 'Tamaño',
  'cargo': 'Capacidad de carga'
};

interface SortConfig {
  field: 'name' | 'size' | 'cargo';
  direction: 'asc' | 'desc';
}

type ShipSize = 'vehicle' | 'v' | 'snub' | 'n' | 'small' | 's' | 'medium' | 'm' | 'large' | 'l' | 'capital' | 'c';

interface TutorialProps {
  isActive: boolean;
  hasNeverCompletedTutorial: () => boolean;
  startTutorial: () => void;
  triggerStepAction: (targetElement: string) => void;
}

interface ShipsPageProps {
  tutorialProps?: TutorialProps;
}

export const ShipsPage: React.FC<ShipsPageProps> = ({ tutorialProps }) => {
  const { data: ships, loading: shipsLoading, error: shipsError } = useApi<Ship[]>('/naves');
  const { data: manufacturers } = useApi<ManufacturerResponse[]>('/naves/marcas');
  const { data: shipTypes } = useApi<string[]>('/naves/tipos');
  const { data: fleetShips } = useApi<Ship[]>('/flota'); // Para contar naves en flota
  const { username } = useUser();
  const { getUserData } = useAuth();
  const userData = getUserData();
  const [localUsername, setLocalUsername] = useState<string>(username || '');
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [currentManufacturer, setCurrentManufacturer] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(true);
  const [showManufacturers, setShowManufacturers] = useState(true);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 16; // Número de items por página
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [shipsToCompare, setShipsToCompare] = useState<Ship[]>([]);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [showSizeMenu, setShowSizeMenu] = useState(false);
  const [selectedFocus, setSelectedFocus] = useState<string>('');
  const [showFocusMenu, setShowFocusMenu] = useState(false);
  const [selectedProductionStatus, setSelectedProductionStatus] = useState<string>('');
  const [showProductionStatusMenu, setShowProductionStatusMenu] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'name', direction: 'asc' });
  const [viewType, setViewType] = useState<'grid' | 'list' | 'catalog'>('grid');
  const [catalogSortBy, setCatalogSortBy] = useState<'manufacturer' | 'type' | 'name'>('manufacturer');
  const [isGrouped] = useState(true);
  const [isUpdatingMatrix, setIsUpdatingMatrix] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [focusPage, setFocusPage] = useState(0);
  
  // Estado y lógica del menú hamburguesa
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cerrar menú al cambiar de ruta
  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  const { showToast } = useToast();

  const isAdmin = userData?.admin === true;

  // Tutorial props
  const {
    hasNeverCompletedTutorial = () => false,
    startTutorial = () => {},
    triggerStepAction = () => {}
  } = tutorialProps || {};

  // Función para manejar el progreso del tutorial en el buscador
  const handleTutorialProgress = (value: string) => {
    const trimmedValue = value.trim().toLowerCase();
    // debugLog('ShipsPage tutorial progress:', value, 'trimmed:', trimmedValue); // Logs desactivados
    if (trimmedValue === 'aurora') {
      // debugLog('Aurora detected in ShipsPage! Triggering step action'); // Logs desactivados
      triggerStepAction('search-bar');
    }
  };

  // Resetear el tipo seleccionado cuando cambia el fabricante
  React.useEffect(() => {
    setSelectedType('');
  }, [selectedManufacturer]);

  // Actualizar el username local cuando cambia el username del hook
  useEffect(() => {
    if (username) {
      setLocalUsername(username);
    }
  }, [username]);

  useEffect(() => {
    if (userData) {
      setIsAnonymous(false);
    }
  }, [userData]);

  // Filtrar primero las naves
  const filteredShips = React.useMemo(() => {
    if (!ships) return [];
    return ships.filter(ship => {
      const matchManufacturer = currentManufacturer === 'all' ? true :
        ship.manufacturer.code === currentManufacturer;
      const matchType = selectedType ? 
        (ship.type || 'undefined')?.toLowerCase() === selectedType.toLowerCase() : true;
      const matchSize = selectedSize ? 
        ship.size?.toLowerCase() === selectedSize.toLowerCase() : true;
      const matchFocus = selectedFocus ? 
        (ship.focus || 'undefined')?.toLowerCase() === selectedFocus.toLowerCase() : true;
      const matchProductionStatus = selectedProductionStatus ? 
        ship.production_status?.toLowerCase() === selectedProductionStatus.toLowerCase() : true;
      const matchSearch = searchQuery.length >= 3 ? 
        ship.name.toLowerCase().includes(searchQuery.toLowerCase()) : true;

      return matchManufacturer && matchType && matchSize && matchFocus && matchProductionStatus && matchSearch;
    });
  }, [ships, currentManufacturer, selectedType, selectedSize, selectedFocus, selectedProductionStatus, searchQuery]);

  // Ordenar las naves filtradas
  const sortedShips = React.useMemo(() => {
    return [...filteredShips].sort((a, b) => {
      let comparison = 0;
      switch (sortConfig.field) {
        case 'name':
          comparison = sortConfig.direction === 'asc' ?
            a.name.localeCompare(b.name) :
            b.name.localeCompare(a.name);
          break;
        case 'size': {
          const sizeOrder: Record<ShipSize, number> = {
            'vehicle': 1, 'v': 1,
            'snub': 2, 'n': 2,
            'small': 3, 's': 3,
            'medium': 4, 'm': 4,
            'large': 5, 'l': 5,
            'capital': 6, 'c': 6
          };
          const aSize = (a.size || '').toLowerCase() as ShipSize;
          const bSize = (b.size || '').toLowerCase() as ShipSize;
          const aValue = sizeOrder[aSize] || 0;
          const bValue = sizeOrder[bSize] || 0;
          comparison = sortConfig.direction === 'asc' ? 
            ((aValue - bValue) || a.name.localeCompare(b.name)) : 
            ((bValue - aValue) || a.name.localeCompare(b.name));
          break;
        }
        case 'cargo': {
          const aCargo = a.cargo_capacity || 0;
          const bCargo = b.cargo_capacity || 0;
          comparison = sortConfig.direction === 'asc' ?
            (aCargo - bCargo || a.name.localeCompare(b.name)) :
            (bCargo - aCargo || a.name.localeCompare(b.name));
          break;
        }
      }
      return comparison;
    });
  }, [filteredShips, sortConfig]);

  // Luego paginar los resultados ordenados
  const paginatedShips = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedShips.slice(startIndex, endIndex);
  }, [sortedShips, currentPage, itemsPerPage]);

  // Resetear la página cuando cambian los filtros
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedManufacturer, selectedType, selectedSize, selectedProductionStatus, searchQuery]);

  const handleTypeChange = React.useCallback(
    (value: string) => {
      setSelectedType(value);
    },
    [setSelectedType]
  );

  const handleSizeChange = React.useCallback(
    (value: string) => {
      setSelectedSize(value);
    },
    [setSelectedSize]
  );

  const handleProductionStatusChange = React.useCallback(
    (value: string) => {
      setSelectedProductionStatus(value);
    },
    [setSelectedProductionStatus]
  );

  // Calcular conteos de naves en la flota (separado por usuario actual y otros usuarios)
  const fleetCounts = React.useMemo(() => {
    if (!fleetShips) return { user: {}, others: {} };
    const userCounts: Record<string, number> = {};
    const otherCounts: Record<string, number> = {};
    
    fleetShips.forEach(ship => {
      if (ship.usuario === localUsername) {
        userCounts[ship.name] = (userCounts[ship.name] || 0) + 1;
      } else {
        otherCounts[ship.name] = (otherCounts[ship.name] || 0) + 1;
      }
    });
    
    return { user: userCounts, others: otherCounts };
  }, [fleetShips, localUsername]);

  // Agrupar naves por fabricante
  const shipsByManufacturer = React.useMemo(() => {
    if (!ships) return {};
    return ships.reduce((acc, ship) => {
      const manufacturer = ship.manufacturer.code;
      if (!acc[manufacturer]) {
        acc[manufacturer] = {
          name: ship.manufacturer.name,
          code: manufacturer,
          ships: []
        };
      }
      acc[manufacturer].ships.push(ship);
      return acc;
    }, {} as Record<string, { name: string; code: string; ships: Ship[] }>);
  }, [ships]);

  const handleClearFilters = () => {
    setSelectedManufacturer('');
    setSelectedType('');
    setSelectedSize('');
    setSelectedProductionStatus('');
    setSearchQuery('');
  };

  const handleCompareToggle = () => {
    setIsCompareMode(!isCompareMode);
    if (isCompareMode) {
      setShipsToCompare([]);
    }
  };

  const handleShipCompareSelect = (ship: Ship) => {
    if (shipsToCompare.some(s => s.name === ship.name)) {
      setShipsToCompare(shipsToCompare.filter(s => s.name !== ship.name));
    } else {
      if (shipsToCompare.length >= 4) {
        alert('Ya has seleccionado el máximo de 4 naves para comparar');
        return;
      }
      setShipsToCompare([...shipsToCompare, ship]);
    }
  };

  const handleManufacturersToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowManufacturers(!showManufacturers);
  };

  const handleFiltersToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowFilters(!showFilters);
  };

  const toggleTypeMenu = () => setShowTypeMenu(!showTypeMenu);
  const toggleSizeMenu = () => setShowSizeMenu(!showSizeMenu);
  const toggleFocusMenu = () => setShowFocusMenu(!showFocusMenu);
  const toggleProductionStatusMenu = () => setShowProductionStatusMenu(!showProductionStatusMenu);

  const handleUpdateMatrix = async () => {
    try {
      setIsUpdatingMatrix(true);
      const response = await fetch('/api/naves/update-matrix', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Error al actualizar la matriz');
      }

      showToast('Matriz actualizada correctamente', 'success');
    } catch (error) {
      showToast('Error al actualizar la matriz', 'error');
    } finally {
      setIsUpdatingMatrix(false);
    }
  };



  if (shipsLoading) return <div className="loading">Cargando naves...</div>;
  if (shipsError) return (
    <div className="error">
      Error al cargar las naves: {shipsError.message}
      <br />
      <small>{shipsError.toString()}</small>
    </div>
  );
  if (!ships) return null;

  return (
    <ProtectedRoute>
      <div className="page">
        <Header 
          onStartTutorial={startTutorial} 
          hasNeverCompletedTutorial={hasNeverCompletedTutorial()}
          onToggleMenu={toggleMenu}
          isMenuOpen={isMenuOpen}
        />
        
        <nav className="navigation-section" ref={menuRef}>
          <Navigation 
            onCompareToggle={handleCompareToggle}
            isCompareMode={isCompareMode}
            compareCount={shipsToCompare.length}
            showCompareButton={true}
            onUpdateCatalog={handleUpdateMatrix}
            isUpdating={isUpdatingMatrix}
            isMenuOpen={isMenuOpen}
            onCloseMenu={closeMenu}
          />
        </nav>
        
        <div className="search-section">
          <SearchBar 
            onSearch={setSearchQuery}
            onClearFilters={handleClearFilters}
            minChars={3}
            onTutorialProgress={handleTutorialProgress}
          />
        </div>

        <main className="page-content">
            <aside className={`manufacturers-nav ${!showManufacturers ? 'collapsed' : ''}`}>
              <button
                className="collapse-button left"
                onClick={handleManufacturersToggle}
                title={showManufacturers ? "Ocultar fabricantes" : "Mostrar fabricantes"}
              />
              <div className="manufacturers-content">
                <button
                  className={`manufacturer-button all-button ${currentManufacturer === 'all' ? 'active' : ''}`}
                  onClick={() => setCurrentManufacturer('all')}
                >
                  TODOS
                </button>
                {manufacturers?.sort((a, b) => a.name.localeCompare(b.name)).map(manufacturer => (
                  <button
                    key={manufacturer.code}
                    className={`manufacturer-button ${currentManufacturer === manufacturer.code ? 'active' : ''}`}
                    onClick={() => setCurrentManufacturer(manufacturer.code)}
                    data-name={manufacturer.name}
                  >
                    <img 
                      src={`/media/logos/${manufacturer.code}.png`} 
                      alt={manufacturer.name}
                      className="manufacturer-logo"
                    />
                    <span className="ship-count">
                      {shipsByManufacturer[manufacturer.code]?.ships.length || 0}
                    </span>
                  </button>
                ))}
              </div>
            </aside>

            <section className="ships-container">
              {/* Controles de vista y ordenación */}
              <div className="view-controls">
                <div className="view-type-selector">
                  <button
                    className={`view-type-btn ${viewType === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewType('grid')}
                    title="Vista de tarjetas"
                  >
                    Tarjetas
                  </button>
                  <button
                    className={`view-type-btn ${viewType === 'list' ? 'active' : ''}`}
                    onClick={() => setViewType('list')}
                    title="Vista de lista"
                  >
                    Lista
                  </button>
                  <button
                    className={`view-type-btn ${viewType === 'catalog' ? 'active' : ''}`}
                    onClick={() => setViewType('catalog')}
                    title="Vista de catálogo"
                  >
                    Catálogo
                  </button>
                </div>
                {viewType === 'catalog' && (
                  <div className="catalog-sort-selector">
                    <label>Ordenar por:</label>
                    <select
                      value={catalogSortBy}
                      onChange={(e) => setCatalogSortBy(e.target.value as 'manufacturer' | 'type' | 'name')}
                      className="catalog-sort-select"
                    >
                      <option value="manufacturer">Marca</option>
                      <option value="type">Tipo de nave</option>
                      <option value="name">Nombre</option>
                    </select>
                  </div>
                )}
              </div>

              {viewType === 'catalog' ? (
                <ShipCatalogView
                  ships={sortedShips}
                  fleetCounts={fleetCounts}
                  sortBy={catalogSortBy}
                  onShipClick={(ship) => {
                    // Aquí puedes añadir lógica para cuando se hace clic en una nave
                    console.log('Nave seleccionada:', ship);
                  }}
                />
              ) : (
                <>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={Math.ceil(sortedShips.length / itemsPerPage)}
                    onPageChange={setCurrentPage}
                  />
                  {isCompareMode && shipsToCompare.length >= 2 && (
                <div className="comparison-layout">
                  <ShipComparison ships={shipsToCompare} />
                  <div className="compared-ships-list">
                    {shipsToCompare.map(ship => (
                      <ShipCard
                        key={ship.name}
                        ship={ship}
                        onClick={() => {}}
                        duplicateCount={1}
                        username={localUsername}
                        isAnonymous={isAnonymous}
                        showAddButton={false}
                        showRemoveButton={false}
                        showUsers={false}
                        isGrouped={false}
                        showCount={false}
                        isCompareMode={true}
                        onCompareSelect={handleShipCompareSelect}
                        isSelectedForCompare={true}
                        isAdmin={isAdmin}
                      />
                    ))}
                  </div>
                </div>
              )}
                  <ShipList 
                    ships={paginatedShips}
                    pagination={{
                      currentPage,
                      totalPages: Math.ceil(sortedShips.length / itemsPerPage),
                      onPageChange: setCurrentPage
                    }}
                    onShipSelect={() => {}}
                    viewType={viewType as 'grid' | 'list'}
                username={localUsername}
                isAnonymous={isAnonymous}
                showUsers={false}
                isGrouped={isGrouped}
                showCount={false}
                isCompareMode={isCompareMode}
                onCompareSelect={handleShipCompareSelect}
                selectedForCompare={shipsToCompare.map(s => s.name)}
                selectedType={selectedType}
                selectedSize={selectedSize}
                isAdmin={isAdmin}
                  />
                </>
              )}
            </section>

            <aside className={`filters-nav ${!showFilters ? 'collapsed' : ''}`}>
              <button
                className="collapse-button right"
                onClick={handleFiltersToggle}
                title={showFilters ? "Ocultar filtros" : "Mostrar filtros"}
              />
              <div className="filters-content">
                <div className="filter-section">
                  <h3>Ordenación</h3>
                  <div className="filter-content">
                    <select 
                      className="filter-select"
                      value={`${sortConfig.field}-${sortConfig.direction}`}
                      onChange={(e) => {
                        const [field, direction] = e.target.value.split('-');
                        setSortConfig({ 
                          field: field as SortConfig['field'], 
                          direction: direction as SortConfig['direction'] 
                        });
                      }}
                    >
                      {Object.entries(SORT_OPTIONS).map(([field, label]) => (
                        <React.Fragment key={field}>
                          <option value={`${field}-asc`}>{label} (A-Z)</option>
                          <option value={`${field}-desc`}>{label} (Z-A)</option>
                        </React.Fragment>
                      ))}
                    </select>
                  </div>
                </div>

                <div className={`filter-section ${!showProductionStatusMenu ? 'collapsed' : ''}`}>
                  <h3 onClick={toggleProductionStatusMenu}>
                    Estado de Producción <FaCaretDown className={`caret ${!showProductionStatusMenu ? 'rotated' : ''}`} />
                  </h3>
                  <div className="filter-content">
                    <div className="filter-buttons">
                      <button
                        className={`filter-button ${selectedProductionStatus === '' ? 'active' : ''}`}
                        onClick={() => handleProductionStatusChange('')}
                      >
                        Todos
                        <span className="count">
                          {ships?.length || 0}
                        </span>
                      </button>
                      {Object.entries(PRODUCTION_STATUS_TRANSLATIONS).map(([statusKey, statusLabel]) => (
                        <button
                          key={statusKey}
                          className={`filter-button ${selectedProductionStatus === statusKey ? 'active' : ''}`}
                          onClick={() => handleProductionStatusChange(statusKey)}
                        >
                          {statusLabel}
                          <span className="count">
                            {ships?.filter(ship => 
                              (ship.production_status || 'undefined')?.toLowerCase() === statusKey.toLowerCase()
                            ).length || 0}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className={`filter-section ${!showTypeMenu ? 'collapsed' : ''}`}>
                  <h3 onClick={toggleTypeMenu}>
                    Tipos <FaCaretDown className={`caret ${!showTypeMenu ? 'rotated' : ''}`} />
                  </h3>
                  <div className="filter-content">
                    <div className="filter-buttons">
                      <button
                        className={`filter-button ${selectedType === '' ? 'active' : ''}`}
                        onClick={() => handleTypeChange('')}
                      >
                        Todos
                        <span className="count">
                          {ships?.length || 0}
                        </span>
                      </button>
                      {shipTypes?.map(type => {
                        const typeKey = (type || 'undefined').toLowerCase();
                        return (
                          <button
                            key={typeKey}
                            className={`filter-button ${selectedType === type ? 'active' : ''}`}
                            onClick={() => handleTypeChange(type)}
                          >
                            {TYPE_TRANSLATIONS[typeKey] || type || 'Sin tipo'}
                            <span className="count">
                              {ships?.filter(ship => 
                                (ship.type || 'undefined')?.toLowerCase() === typeKey
                              ).length || 0}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className={`filter-section ${!showSizeMenu ? 'collapsed' : ''}`}>
                  <h3 onClick={toggleSizeMenu}>
                    Tamaños <FaCaretDown className={`caret ${!showSizeMenu ? 'rotated' : ''}`} />
                  </h3>
                  <div className="filter-content">
                    <div className="filter-buttons">
                      <button
                        className={`filter-button ${selectedSize === '' ? 'active' : ''}`}
                        onClick={() => handleSizeChange('')}
                      >
                        Todos
                        <span className="count">
                          {ships?.length || 0}
                        </span>
                      </button>
                      {Object.entries(SIZE_TRANSLATIONS).map(([englishKey, spanishLabel]) => (
                        <button
                          key={englishKey}
                          className={`filter-button ${selectedSize === englishKey ? 'active' : ''}`}
                          onClick={() => handleSizeChange(englishKey)}
                        >
                          {spanishLabel}
                          <span className="count">
                            {ships?.filter(ship => 
                              ship.size?.toLowerCase() === englishKey.toLowerCase()
                            ).length || 0}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className={`filter-section ${!showFocusMenu ? 'collapsed' : ''}`}>
                  <h3 onClick={toggleFocusMenu}>
                    Enfoque <FaCaretDown className={`caret ${!showFocusMenu ? 'rotated' : ''}`} />
                  </h3>
                  <div className="filter-content">
                    {(() => {
                      const uniqueFocuses = Array.from(new Set(ships?.map(ship => ship.focus)));
                      const itemsPerPage = 10;
                      const totalPages = Math.ceil(uniqueFocuses.length / itemsPerPage);
                      const startIndex = focusPage * itemsPerPage;
                      const currentFocuses = uniqueFocuses.slice(startIndex, startIndex + itemsPerPage);

                      const handlePrevPage = () => {
                        setFocusPage(prev => Math.max(0, prev - 1));
                      };

                      const handleNextPage = () => {
                        setFocusPage(prev => Math.min(totalPages - 1, prev + 1));
                      };

                      return (
                        <>
                          {totalPages > 1 && (
                            <div className="pagination-controls">
                              <button 
                                className="pagination-arrow" 
                                onClick={handlePrevPage}
                                disabled={focusPage === 0}
                                title="Página anterior"
                              >
                                <FaChevronUp />
                              </button>
                              <span className="pagination-info">
                                {focusPage + 1}/{totalPages}
                              </span>
                              <button 
                                className="pagination-arrow" 
                                onClick={handleNextPage}
                                disabled={focusPage === totalPages - 1}
                                title="Página siguiente"
                              >
                                <FaChevronDown />
                              </button>
                            </div>
                          )}
                          <div className="filter-buttons">
                            <button
                              className={`filter-button ${selectedFocus === '' ? 'active' : ''}`}
                              onClick={() => setSelectedFocus('')}
                            >
                              Todos
                              <span className="count">
                                {ships?.length || 0}
                              </span>
                            </button>
                            {currentFocuses.map(focus => (
                              <button
                                key={focus}
                                className={`filter-button ${selectedFocus === focus ? 'active' : ''}`}
                                onClick={() => setSelectedFocus(focus)}
                              >
                                {focus || 'Sin enfoque'}
                                <span className="count">
                                  {ships?.filter(ship => ship.focus === focus).length || 0}
                                </span>
                              </button>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

              </div>
            </aside>
        </main>

      </div>
    </ProtectedRoute>
  );
}; 